import {
  ApiBrandType,
  FeedbackContextDto,
  PublicReportDto,
} from '../types/models.types';
import { formatDateToDDMMYYYY } from './car-basic-info.mapper';
import {
  mapCarBasicInfoSectionToUI,
  MappedCarBasicInfo,
} from './car-basic-info.mapper';
import {
  mapReportSummaryToUI,
  MappedReportSummary,
} from './report-summary.mapper';
import {
  mapCarBodySectionToUI,
  MappedCarBodySection,
} from './car-body-section.mapper';
import {
  mapCarInteriorSectionToUI,
  MappedCarInteriorSection,
} from './car-interior-section.mapper';
import {
  mapEngineAndTrunkSectionToUI,
  MappedEngineAndTrunkSection,
} from './engine-trunk-section.mapper';
import {
  mapTestDriveSectionToUI,
  MappedTestDriveSection,
} from './test-drive-section.mapper';
import {
  mapChassisSectionToUI,
  MappedChassisSection,
} from './chassis-section.mapper';
import {
  mapComputerDiagnosticsSectionToUI,
  MappedComputerDiagnosticsSection,
} from './computer-diagnostics-section.mapper';
import {
  mapRepairHistorySectionToUI,
  MappedRepairHistorySection,
} from './repair-history-section.mapper';
import { mapGalleriesToUI, MappedGalleries } from './gallery.mapper';
import {
  mapCepikSectionToUI,
  MappedCepikSection,
} from './cepik-section.mapper';
import { mapAddonsToUI, MappedAddon } from './addons.mapper';
import { ApiResponseRaportV2 } from './hsp/api';

/**
 * Placeholder zwracany przez backend HSP gdy konkretna wartość (brand/model)
 * nie jest jeszcze znana. Traktujemy go jak brak danych.
 */
const HSP_MISSING_INFO_PLACEHOLDER = 'missing-info';

const TEMP_RETURNING_FEEDBACK_CONTEXT_OVERRIDE: FeedbackContextDto = {
  canVote: true,
  alreadyVoted: false,
  successfulLoginCount: 2,
};

const sanitizeHspValue = (value?: string): string => {
  if (!value || value === HSP_MISSING_INFO_PLACEHOLDER) {
    return '';
  }
  return value;
};

/**
 * Wyjątkowy scenariusz: backend zwrócił tylko sekcję HSP oraz `id` raportu,
 * wszystkie pozostałe sekcje raportu z oględzin są puste (`null`).
 * Wtedy UI pokazujemy w ograniczonej formie:
 *  - `RaportHeader` z danymi pobranymi z HSP (VIN, data utworzenia, marka/model jeśli znane),
 *  - bez `RaportHeroSection` ("Wstępne wyniki ekspertyzy...").
 */
type PublicReportDtoWithHspSection = PublicReportDto & {
  hspSection: ApiResponseRaportV2;
};

const isHspOnlyReport = (report: PublicReportDtoWithHspSection): boolean => {
  return Boolean(report.hspSection) && !report.carBasicInfoSection;
};

/**
 * Wyjątkowy scenariusz: backend zwrócił sam `id` raportu, bez sekcji HSP
 * i bez `carBasicInfoSection` (np. raport ze statusem `REPORT_EDITING`,
 * jeszcze przed zebraniem danych). UI musi wtedy ukryć `RaportHeader`,
 * bo nie mamy nawet VIN-u do pokazania.
 */
const isEmptyReport = (report: PublicReportDtoWithHspSection): boolean => {
  return !report.hspSection && !report.carBasicInfoSection;
};

/**
 * Statusy, w których raport jest gotowy do zaprezentowania klientowi w pełnej
 * formie (`RaportHeader` zamiast ekranu wstępnego `RaportHeroSection`).
 *
 * `REPORT_FINAL_PUBLISHED` to stan końcowy następujący PO `REPORT_VERIFIED`
 * (raport upubliczniony — patrz API: "Zmienia status raportu z REPORT_VERIFIED
 * na REPORT_FINAL_PUBLISHED"), dlatego również musi być traktowany jako
 * zweryfikowany. Listę trzymamy w jednym miejscu, żeby łatwo ją rozszerzać.
 */
export const VERIFIED_REPORT_STATUSES = [
  'REPORT_VERIFIED',
  'REPORT_FINAL_PUBLISHED',
] as const;

/**
 * Czy raport jest w statusie pozwalającym pokazać pełną wersję (po weryfikacji).
 */
export const isVerifiedReportStatus = (status: string): boolean =>
  (VERIFIED_REPORT_STATUSES as readonly string[]).includes(status);

export type AvailableSection =
  | 'CAR_BASIC_INFO'
  | 'EXTERNAL_INSPECTION'
  | 'INTERNAL_INSPECTION'
  | 'ENGINE_AND_TRUNK_COMPARTMENT'
  | 'TEST_DRIVE'
  | 'CAR_CHASSIS'
  | 'COMPUTER_DIAGNOSTICS'
  | 'REPAIR_HISTORY'
  | 'REPORT_SUMMARY';

export interface MappedPublicReport {
  hspSection?: ApiResponseRaportV2;
  id: string;
  createdAt: string | null;
  reportStatus: string;
  reportType: string;
  availableSections: Set<AvailableSection>;
  carBasicInfo: MappedCarBasicInfo;
  carBody: MappedCarBodySection | null;
  carInterior: MappedCarInteriorSection | null;
  engineAndTrunk: MappedEngineAndTrunkSection | null;
  testDrive: MappedTestDriveSection | null;
  chassis: MappedChassisSection | null;
  computerDiagnostics: MappedComputerDiagnosticsSection | null;
  repairHistory: MappedRepairHistorySection | null;
  summary: MappedReportSummary | null;
  galleries: MappedGalleries;
  cepikSection: MappedCepikSection;
  addons: MappedAddon[];
  /** URL do pliku wideo z oględzin (S3) */
  videoUrl?: string;
  /** URL do miniaturki wideo */
  videoThumbnailUrl?: string;
  externalOrderId: string;
  /** Typ marki (brand) raportu — decyduje m.in. o logo w UI. */
  brandType?: ApiBrandType;
  /** Kontekst głosowania klienta przekazany z API bez logiki UI. */
  feedbackContext?: FeedbackContextDto;
  /**
   * Sygnalizuje wyjątkowy scenariusz: w odpowiedzi z API obecna jest tylko
   * sekcja HSP (oraz `id` raportu). Dane podstawowe pojazdu są wtedy
   * uzupełniane na podstawie `hspSection.content.basicCarInformationDto`,
   * a UI ukrywa sekcje opisujące raport z oględzin (np. `RaportHeroSection`).
   */
  isHspOnly: boolean;
  /**
   * Sygnalizuje, że w odpowiedzi z API nie ma ani sekcji HSP, ani danych
   * podstawowych pojazdu (`carBasicInfoSection`). UI ukrywa wtedy `RaportHeader`,
   * bo nie ma czego w nim pokazać (brak VIN-u, daty raportu, marki/modelu).
   */
  isEmpty: boolean;
}

export const mapPublicReportToUI = (
  report: PublicReportDtoWithHspSection | null | undefined,
): MappedPublicReport => {
  if (!report) {
    throw new Error('Nie udało się zmapować danych raportu publicznego');
  }
  const availableSections = new Set<AvailableSection>(
    (report.availableSections as AvailableSection[] | undefined) ?? [],
  );

  const hspOnly = isHspOnlyReport(report);
  const hspBasicCarInfo = report.hspSection?.content?.basicCarInformationDto;

  // Dla scenariusza HSP-only uzupełniamy minimalny zestaw danych pojazdu (VIN, marka, model)
  // bezpośrednio z HSP, bo `carBasicInfoSection` z oględzin nie istnieje.
  const carBasicInfo = mapCarBasicInfoSectionToUI(report.carBasicInfoSection);
  if (hspOnly && hspBasicCarInfo) {
    carBasicInfo.vinNumber = sanitizeHspValue(hspBasicCarInfo.vin);
    carBasicInfo.vehicleBrand = sanitizeHspValue(hspBasicCarInfo.brand);
    carBasicInfo.vehicleModel = sanitizeHspValue(hspBasicCarInfo.model);
  }

  // W trybie HSP-only `report.createdAt` jest puste — bierzemy datę utworzenia raportu HSP.
  const rawCreatedAt =
    report.createdAt ?? (hspOnly ? report.hspSection?.createdDate : undefined);
  const reportWithVideo = report as PublicReportDtoWithHspSection & {
    videoUrl?: string;
    videoThumbnailUrl?: string;
  };

  return {
    id: report.id || '',
    externalOrderId: report.externalOrderId || '',
    createdAt: rawCreatedAt ? formatDateToDDMMYYYY(rawCreatedAt) : null,
    reportStatus: report.reportStatus || 'NEW',
    reportType: report.reportTypeDisplayName || '',
    brandType: report.brandType,
    feedbackContext: {
      ...report.feedbackContext,
    },
    availableSections,
    isHspOnly: hspOnly,
    isEmpty: isEmptyReport(report),

    // Orchestrate section mappers
    carBasicInfo,
    carBody: mapCarBodySectionToUI(report.carBodySection),
    carInterior: mapCarInteriorSectionToUI(report.carInteriorSection),
    engineAndTrunk: mapEngineAndTrunkSectionToUI(
      report.engineAndTrunkCompartmentSection,
    ),
    testDrive: mapTestDriveSectionToUI(report.carTestDriveSection),
    chassis: mapChassisSectionToUI(report.carChassisSection),
    computerDiagnostics: mapComputerDiagnosticsSectionToUI(
      report.computerDiagnosticsSection,
    ),
    repairHistory: mapRepairHistorySectionToUI(report.repairHistorySection),
    summary: mapReportSummaryToUI(report),
    galleries: mapGalleriesToUI(report.galleries),
    cepikSection: mapCepikSectionToUI(report.cepikSection),
    addons: mapAddonsToUI(report.addonsSection),

    hspSection: report.hspSection,

    // Video z oględzin (jeśli dostępne w API)
    videoUrl: reportWithVideo.videoUrl || undefined,
    videoThumbnailUrl: reportWithVideo.videoThumbnailUrl || undefined,
  };
};
